import React, { useCallback, useState, useRef } from 'react';
import { Alert, Button, SpaceBetween, Box } from '@amzn/awsui-components-react';
import deviceStatusHandler from '../../services/deviceStatusHandler';
import ReportComponentFailure from './reportComponentFailure';
import ComponentInstallationDetails from './componentInstallationDetails';
import EmeraldInformationTable from './emeraldInformationTable';
import { ComponentType } from './componentType';

const EXPECTED_HEALTH_STATUS = 'P2';

const formatTimestamp = (timestampSecond?: number): string => {
    return timestampSecond
        ? new Date(timestampSecond * 1000).toLocaleString(undefined, { timeZoneName: 'short' })
        : 'Not available';
};

const checkDeviceHealth = (data: any) => {
    const latestStatusCodes = data?.latestStatusCodes || [];
    const emeraldInformation = data?.emeraldInformation;
    const emeraldInstalled = emeraldInformation?.cycloneInstalled || false;

    // Device is healthy if:
    // 1. No status codes present (treat missing as success) AND emerald is installed, OR
    // 2. P2 status is present AND emerald is installed
    const hasP2Status = latestStatusCodes.includes(EXPECTED_HEALTH_STATUS);
    const hasNoStatusCodes = latestStatusCodes.length === 0;
    const isHealthy = (hasP2Status || hasNoStatusCodes) && emeraldInstalled;

    // Create display status - either emerald not running or actual status codes
    let healthStatusDisplay;
    if (!emeraldInstalled) {
        healthStatusDisplay = 'Emerald Diagnostics not running';
    } else if (hasNoStatusCodes || hasP2Status) {
        healthStatusDisplay = 'Healthy';
    } else {
        healthStatusDisplay = latestStatusCodes.join(', ');
    }

    return {
        isHealthy,
        healthStatusDisplay,
    };
};

const renderHealthWarningContent = (
    lastStatusTimestampString?: string,
    emeraldRunning?: boolean,
    statusCodes?: string[],
) => (
    <SpaceBetween size="xs">
        <div>
            <strong>
                {!emeraldRunning
                    ? 'Emerald Diagnostics not running'
                    : `Error status codes: ${statusCodes?.join(', ') || 'Unknown'}`}
            </strong>
        </div>
        <div>
            Last detected: <strong>{lastStatusTimestampString || 'Not available'}</strong>.
        </div>
        <div>
            {!emeraldRunning
                ? 'Reboot the fleet edge box and wait for up to 10 minutes, then click "Retry Health Check".'
                : 'Follow SOP to troubleshoot the device, then click "Retry Health Check".'}{' '}
            If the installation portal is still showing unhealthy after 10 minutes, please report the failure using the
            "Report Installation Failure" button.
        </div>
        <div style={{ color: 'red' }}>
            <strong>Note:</strong> Do not rely on the fleet edge LED status code for health check.
        </div>
    </SpaceBetween>
);

interface EmeraldHealthCheckerProps {
    deviceId: string;
    vin: string;
    onHealthCheckPassed: (emeraldInformation?: any) => void;
    onReportFailure: () => void;
    componentType: ComponentType;
    serialNumber: string;
}

const EmeraldHealthChecker: React.FC<EmeraldHealthCheckerProps> = ({
    deviceId,
    vin,
    onHealthCheckPassed,
    onReportFailure,
    componentType,
    serialNumber,
}) => {
    const [isChecking, setIsChecking] = useState(false);
    const [healthStatus, setHealthStatus] = useState<string | null>(null);
    const [healthCheckError, setHealthCheckError] = useState<string | null>(null);
    const [showRetryOptions, setShowRetryOptions] = useState(false);
    const [showReportFailure, setShowReportFailure] = useState(false);
    const [isHealthy, setIsHealthy] = useState(false);
    const [emeraldInformation, setEmeraldInformation] = useState<any>(null);
    const [latestStatusCodes, setLatestStatusCodes] = useState<string[]>([]);
    const hasPerformedInitialCheck = useRef(false);

    const performHealthCheck = useCallback(() => {
        setIsChecking(true);
        setHealthCheckError(null);
        setHealthStatus(null);
        setShowRetryOptions(false);

        deviceStatusHandler(
            deviceId,
            vin,
            false,
            (success: boolean, data: any) => {
                setIsChecking(false);

                if (!success) {
                    setHealthCheckError(
                        'Failed to verify device health status. Please check device connectivity and try again.',
                    );
                    setShowRetryOptions(true);
                    return;
                }

                const healthCheckResult = checkDeviceHealth(data);

                setEmeraldInformation(data?.emeraldInformation || null);
                setLatestStatusCodes(data?.latestStatusCodes || []);
                setHealthStatus(healthCheckResult.healthStatusDisplay);
                setIsHealthy(healthCheckResult.isHealthy);

                if (healthCheckResult.isHealthy) {
                    // Health check passed, proceed with installation completion
                    onHealthCheckPassed(data?.emeraldInformation);
                } else {
                    // Health check failed, show warning and retry options
                    setShowRetryOptions(true);
                }
            },
            true,
        );
    }, [deviceId, vin, onHealthCheckPassed]);

    // Automatically perform health check when component mounts
    React.useEffect(() => {
        if (!hasPerformedInitialCheck.current) {
            hasPerformedInitialCheck.current = true;
            performHealthCheck();
        }
    }, [performHealthCheck]);

    const handleRetryHealthCheck = useCallback(() => {
        performHealthCheck();
    }, [performHealthCheck]);

    const handleReportFailure = useCallback(() => {
        setShowReportFailure(true);
    }, []);

    const handleFailureReported = useCallback(() => {
        onReportFailure();
    }, [onReportFailure]);

    const handleCancelReportFailure = useCallback(() => {
        setShowReportFailure(false);
    }, []);

    // Show the report failure component if user clicked report failure
    if (showReportFailure) {
        const reportTimestamp = formatTimestamp(emeraldInformation?.timestampSecond);

        return (
            <ReportComponentFailure
                deviceId={deviceId}
                vin={vin}
                componentType={componentType}
                serialNumber={serialNumber}
                onFailureReported={handleFailureReported}
                onCancel={handleCancelReportFailure}
                defaultFailureReason="health_check_failed"
                defaultNotes={`Device health check failed. Status: ${
                    healthStatus || 'No status available'
                }. Last detected: ${reportTimestamp}`}
            />
        );
    }

    if (isChecking) {
        return (
            <SpaceBetween size="l">
                <Box variant="h2">Verifying Device Health</Box>
                <Alert type="info" header="Health Check in Progress">
                    Verifying device health status... This process might take a few minutes. Please keep this page open
                    and wait for the verification to complete.
                </Alert>
            </SpaceBetween>
        );
    }

    if (healthCheckError || (showRetryOptions && !isHealthy)) {
        const isError = !!healthCheckError;
        const title = isError ? 'Device Health Verification' : 'Device Health Warning';
        const alertType = isError ? 'error' : 'warning';
        const alertHeader = isError ? 'Health Check Failed' : 'Device Not Healthy';
        const lastStatusTimestampString = formatTimestamp(emeraldInformation?.timestampSecond);

        const emeraldRunning = emeraldInformation?.cycloneInstalled || false;

        const alertContent = isError
            ? healthCheckError
            : renderHealthWarningContent(lastStatusTimestampString, emeraldRunning, latestStatusCodes);

        return (
            <SpaceBetween size="l">
                <Box variant="h2">{title}</Box>

                <Alert type={alertType} header={alertHeader}>
                    {alertContent}
                </Alert>

                {!isError && (
                    <>
                        <ComponentInstallationDetails
                            deviceId={deviceId}
                            vin={vin}
                            componentType={componentType}
                            status={healthStatus || 'Unknown'}
                            title="Component Details"
                            lastStatusTimestampString={lastStatusTimestampString}
                        />

                        {emeraldInformation && <EmeraldInformationTable emeraldInformation={emeraldInformation} />}
                    </>
                )}

                <SpaceBetween size="s" direction="horizontal">
                    <Button variant="primary" onClick={handleRetryHealthCheck}>
                        Retry Health Check
                    </Button>
                    <Button variant="normal" onClick={handleReportFailure}>
                        Report Installation Failure to Amazon
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        );
    }

    // This should not be reached as the component will call onHealthCheckPassed when P2 is detected
    return null;
};

export default EmeraldHealthChecker;
